import type { tAlertSnapshot } from 'src/alerts/AlertCenter.types';
import type {
  tAlertSnapshotOverride,
  tAlertSnapshotPatch,
  tAlertSnapshotRawInputs,
} from './AlertCenter.types';

/**
 * Whitelist of `tAlertSnapshot` keys a patch is allowed to touch. Any other
 * key in a stored payload is dropped rather than passed through — the
 * payload originates from localStorage, so a stale schema or a hand-edited
 * value must never smuggle an unrelated field into the assembled snapshot.
 *
 * 🔴 IT IS A HAND-MAINTAINED MIRROR OF `tAlertSnapshot`, AND IT HAD DRIFTED TEN
 * KEYS BEHIND (found 2026-09-11, U12). The failure is silent and specific: the
 * anti-rot preset test calls `applyAlertSnapshotOverride` DIRECTLY, so a preset
 * whose keys are missing here passes every test and works in the browser —
 * until you reload, when the parser drops them and the alert quietly stops
 * firing. `stage-threshold-explainer` had been in that state, losing all five
 * of its board fields.
 *
 * Exported so a test can assert the mirror covers every key of a real
 * snapshot; a mirror nothing checks is not a guarantee.
 */
export const SNAPSHOT_KEYS = new Set<keyof tAlertSnapshot>([
  'workspaceId',
  'role',
  'surface',
  // RET-2452: added WITH the snapshot fields, per the note below.
  'pastDueZoneOnScreen',
  'pastDuePaymentZoneEnabled',
  'activeLeadsCount',
  'activeLeadsLimit',
  'isQuotaEligiblePlan',
  'isLeadLimitAddonEligiblePlan',
  'canAccessWhatsAppApi',
  'hasActivePlan',
  'planName',
  'planPauseActive',
  'planPauseEndsAt',
  'wapiDisconnected',
  'wapiDisconnectCause',
  // INT-1653: added WITH the snapshot field, per the note above.
  'wapiDisconnection',
  'wapiNoAccount',
  // INT-1516: added WITH the snapshot fields, per the note above.
  'wapiActivationPending',
  'wapiActivationFailed',
  'wapiRoutingConflict',
  'whatsAppWebErrorCode',
  'accountHealthStatus',
  'accountHealthViolation',
  'leadAgentExpired',
  'leadAgentDismissedAt',
  'leadAgentPausedWithinTrial',
  'leadAgentAcceptancePending',
  'leadAgentAcceptanceUrgency',
  'leadAgentDaysUntilPayment',
  'leadAgentPaymentDate',
  'leadAgentTrialCapReached',
  'leadAgentTrialCapVariant',
  'leadAgentTrialActivatable',
  'leadAgentCapacityPercent',
  'leadAgentDaysSinceExpiry',
  'leadAgentTier',
  'pendingPayment',
  'sellerPaused',
  'calendarConnectionRevoked',
  // AGI-428: added WITH the snapshot field, per the note below. A key missing
  // from this whitelist survives in memory and is dropped on reload.
  'metaPricingNotice',
  // INT-1522: added WITH the snapshot field, same reason as metaPricingNotice
  // above -- a key missing from this whitelist survives in memory and is
  // dropped on reload.
  'metaTokenInvalidNotice',
  // RET-2191: added WITH the snapshot field, for the same reason.
  'leadAgentCapRaiseNotice',
  'userInactive',
  'isBillingMigrationInProgress',
  // U11 (RET-1269) — the past-due block. Whitelisted NOW, with the
  // declarations still to come: a preset that patches a key missing from this
  // set is silently dropped on the way back out of localStorage, so the
  // simulator would appear to work in its own unit test (which calls
  // `applyAlertSnapshotOverride` directly) and do nothing in the browser.
  'subscriptionPastDue',
  'daysPastDue',
  'pastDueAmount',
  'pastDueCancellationDate',
  // U12 — added with the field itself. This whitelist is NOT derived from
  // `tAlertSnapshot`, so a new snapshot key that is not repeated here is
  // silently dropped by `parseAlertSnapshotOverride` on reload while
  // `applyAlertSnapshotOverride` still honours it in memory: the preset works
  // until you refresh, then quietly stops. See the round-trip test.
  'pastDueCancellationPassed',
  'pastDueInvoiceUrl',
  'pastDueNextPaymentAttempt',
  'pastDueAttempted',
  'pastDueAttemptCount',
  'pastDueCollectionMethod',
  'pastDueDeclineCode',
  'pastDueCardBrand',
  'pastDueCardLast4',
  'billingPeriodId',
  'ownerName',
  'leadAgentShutdownDate',
  'leadAgentInvoiceAmount',
  'leadAgentInvoiceDueDate',
  'leadAgentInvoiceUrl',
  'leadAgentTrialDaysRemaining',
  'subscriptionPromoTotal',
  'subscriptionPromoCurrency',
  'hiddenConversationsCount',
  // U9's board signal. All five were missing, which is why the red-card
  // explainer preset survived a click and not a reload.
  'boardHasExpiredLead',
  'boardExpiredStageId',
  'boardExpiredStageName',
  'boardExpiredLeadName',
  'boardExpiredElapsed',
]);

const CURRENT_VERSION = 1;

/**
 * Parses the persisted override payload. Fails to `null` — never throws —
 * for anything that isn't the exact current schema: malformed JSON, a
 * missing/mismatched `version`, or a non-object root. This runs on every
 * mount of `AlertCenterProvider`, so a throw here is a white screen on every
 * private page, not a missing simulator preset.
 */
export function parseAlertSnapshotOverride(
  raw: string | null,
): tAlertSnapshotOverride | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const candidate = parsed as Record<string, unknown>;
  if (candidate.version !== CURRENT_VERSION) return null;

  const presetId =
    typeof candidate.presetId === 'string' ? candidate.presetId : null;

  let rawInputs: tAlertSnapshotRawInputs | undefined;
  if (candidate.rawInputs && typeof candidate.rawInputs === 'object') {
    const inputs = candidate.rawInputs as Record<string, unknown>;
    rawInputs =
      'subscriptionEndDate' in inputs
        ? { subscriptionEndDate: inputs.subscriptionEndDate }
        : {};
  }

  let patch: tAlertSnapshotPatch | undefined;
  if (candidate.patch && typeof candidate.patch === 'object') {
    const rawPatch = candidate.patch as Record<string, unknown>;
    const whitelisted: tAlertSnapshotPatch = {};
    Object.keys(rawPatch).forEach((key) => {
      if (SNAPSHOT_KEYS.has(key as keyof tAlertSnapshot)) {
        whitelisted[key as keyof tAlertSnapshot] = rawPatch[key];
      }
    });
    patch = whitelisted;
  }

  return { version: CURRENT_VERSION, presetId, rawInputs, patch };
}

/**
 * Overlays `override.patch` onto the real snapshot. The single permitted
 * `as` assertion in this feature, confined here per the
 * `AdvanceSearchModal.functions.ts` precedent: `tAlertSnapshotPatch` is
 * deliberately `unknown`-valued so it can carry a malformed field (the bug
 * class this simulator exists to reproduce), which is exactly why the merged
 * object needs one assertion back to `tAlertSnapshot` at this single,
 * tested boundary instead of scattering casts across every call site.
 *
 * `null`/patch-less override is the identity — real state, untouched.
 */
export function applyAlertSnapshotOverride(
  snapshot: tAlertSnapshot,
  override: tAlertSnapshotOverride | null,
): tAlertSnapshot {
  if (!override?.patch) return snapshot;
  // eslint-disable-next-line no-restricted-syntax -- single permitted assertion; the whole point of this adapter.
  return { ...snapshot, ...override.patch } as tAlertSnapshot;
}
